/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Spinner, Text } from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { IconInfoCircle, IconX, IconArrowLeft } from '@tabler/icons-react'
import { moduleService } from '@/services/modulesService'
import { practiceTestService } from '@/services/practiceTestService'
import {
  DuplicatePracticeTestBody,
  PracticeTestBody,
  PracticeTestStatus,
  UpdatePracticeTestBody,
} from '@/types/practice-test'
import {
  PracticeTestDetail,
  PracticeTestStatus as PTStatus,
} from '@/types/practice-test'
import { CaseStudyQuestion, QuestionDetail } from '@/types/question'
import { toast } from 'sonner'
import { useAuth } from '@/stores/authStore'
import { hoverPressable } from '@/utils/style'
import { getCaseStudyQuestionIdsFromPayload } from '@/utils/practiceTestSectionIds'
import { useHeroNavbar } from '@/context/hero-navbar-context'
import Preview from '@/components/ui/PreviewTest/preview'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import Hstack from '@/components/ui/hstack'
import Stack from '@/components/ui/stack'
import { ConfirmDialog } from '@/components/confirm-dialog'
// import HeaderEdit from '@/components/layout/header-edit'
import CreateView from './components/create-view'

export default function CreateMockExam({
  initialPracticeTestId,
  initialPracticeTestName,
  moduleId,
  status,
  initialDurationMinutes,
  initialTotalQuestions,
  initialTotalMarks,
  initialSelectedQuestions,
  initialSelectedCaseStudies,
  initDuration,
  refetchData,
  initialGeneralInformation,
}: {
  initialPracticeTestId?: string
  initialPracticeTestName?: string
  moduleId: string
  status?: string
  initialDurationMinutes?: number
  initialTotalQuestions?: number
  initialTotalMarks?: number
  initialSelectedQuestions?: Record<string, any[]>
  initialSelectedCaseStudies?: Record<string, any[]>
  initDuration?: number
  refetchData?: () => void
  initialGeneralInformation?: string
}) {
  const navigate = useNavigate()
  const [selectedBySection, setSelectedBySection] = useState<
    Record<string, string[]>
  >({})
  const { setIsActive } = useHeroNavbar()
  const [practiceTestName, setPracticeTestName] = useState<string>(
    initialPracticeTestName ?? ''
  )
  const queryClient = useQueryClient()
  const [practiceTestId, setPracticeTestId] = useState<string>(
    initialPracticeTestId ?? ''
  )
  const [durationMinutes, setDurationMinutes] = useState<number | undefined>(
    initDuration ?? initialDurationMinutes
  )
  const [totalQuestions, setTotalQuestions] = useState<number | undefined>(
    initialTotalQuestions
  )
  const [totalMarks, setTotalMarks] = useState<number | undefined>(
    initialTotalMarks
  )
  const [generalInformation, setGeneralInformation] = useState<string>(
    initialGeneralInformation ?? ''
  )
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false)
  const [sectionTotals, setSectionTotals] = useState<
    Record<
      string,
      { totalQuestions: number; totalMarks: number; totalTime: number }
    >
  >({})

  useEffect(() => {
    if (initialPracticeTestName) {
      setPracticeTestName(initialPracticeTestName)
    }
  }, [initialPracticeTestName])

  useEffect(() => {
    if (initialGeneralInformation) {
      setGeneralInformation(initialGeneralInformation)
    }
  }, [initialGeneralInformation])

  const [selectedItemsBySection, setSelectedItemsBySection] = useState<
    Record<string, QuestionDetail[]>
  >({})
  const [caseStudyGroupsBySection, setCaseStudyGroupsBySection] = useState<
    Record<
      string,
      Array<{
        caseStudyId: number
        items: CaseStudyQuestion[]
        totalTime: number
        totalMarks: number
        selectedCount: number
      }>
    >
  >({})

  function computeSums(
    map: Record<
      string,
      { totalQuestions: number; totalMarks: number; totalTime: number }
    >
  ) {
    let sumQ = 0
    let sumM = 0
    let sumT = 0
    for (const key of Object.keys(map)) {
      const t = map[key]
      sumQ += t?.totalQuestions || 0
      sumM += t?.totalMarks || 0
      sumT += t?.totalTime || 0
    }
    return { sumQ, sumM, sumT }
  }
  const { user } = useAuth()
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false)
  const [isBackConfirmOpen, setIsBackConfirmOpen] = useState<boolean>(false)
  const [isDuplicateOpen, setIsDuplicateOpen] = useState<boolean>(false)

  const { mutateAsync: createPracticeTest, isPending: isCreating } =
    useMutation({
      mutationFn: (body: PracticeTestBody) =>
        practiceTestService.createPracticeTest(body),
    })

  const { mutateAsync: publishPracticeTest, isPending: isPublishing } =
    useMutation({
      mutationFn: (practiceTestId: string) =>
        practiceTestService.publishPracticeTest(practiceTestId),
    })

  const { mutateAsync: deleteDraftPracticeTest, isPending: isDeletingDraft } =
    useMutation({
      mutationFn: (practiceTestId: string) =>
        practiceTestService.deleteDraftPracticeTest(practiceTestId),
    })

  const { mutateAsync: updatePracticeTest, isPending: isUpdating } =
    useMutation({
      mutationFn: ({
        id,
        body,
      }: {
        id: string
        body: UpdatePracticeTestBody
      }) => practiceTestService.updatePracticeTest(id, body),
    })

  const { mutateAsync: duplicatePracticeTest, isPending: isDuplicating } =
    useMutation({
      mutationFn: (body: DuplicatePracticeTestBody) =>
        practiceTestService.duplicatePracticeTest(body),
    })

  // const { data: classDetail } = useQuery({
  //   queryKey: ['classDetail', classId],
  //   queryFn: () => classService.getClassById(Number(classId)),
  //   enabled: !!Number(classId),
  // })

  // Fetch module config to know if section C is required
  const { data: moduleConfig } = useQuery({
    queryKey: ['moduleConfig'],
    queryFn: () => moduleService.getModuleConfig(),
    staleTime: 5 * 60 * 1000,
    enabled: !!moduleId,
  })

  const requiredSections = useMemo(() => {
    const needC = moduleConfig?.need_sheet_module_id?.includes(Number(moduleId))
    return needC ? ['A', 'B', 'C'] : ['A', 'B']
  }, [moduleConfig?.need_sheet_module_id, moduleId])

  function buildUpdateBody(
    statusForUpdate: PracticeTestStatus
  ): UpdatePracticeTestBody {
    const sections: { section: string; question_ids: number[] }[] =
      Object.entries(selectedBySection).map(([section, ids]) => {
        // Get case-study question IDs for this section (from current selection or fallback to initial)
        let caseStudyQuestionIds: number[] = []

        // First, try to get from current case-study selections
        const currentCaseStudyIds = (caseStudyGroupsBySection[section] || [])
          .flatMap((group) => group.items.map((item) => item.question_id))
          .filter((id) => Number.isFinite(id))

        if (currentCaseStudyIds.length > 0) {
          // Use current case-study selections
          caseStudyQuestionIds = currentCaseStudyIds
        } else {
          // Fallback to initial case-study selections from draft
          const initialCaseStudies = initialSelectedCaseStudies?.[section] || []
          caseStudyQuestionIds = initialCaseStudies.flatMap((cs: any) =>
            getCaseStudyQuestionIdsFromPayload(cs)
          )
        }

        // Get regular question IDs (excluding any that are already in case-study questions to avoid duplicates)
        const regularQuestionIds = (ids || [])
          .map((id) => Number(id))
          .filter((n) => Number.isFinite(n))
          .filter((id) => !caseStudyQuestionIds.includes(id))

        // Combine regular question IDs with case-study question IDs
        const allQuestionIds = [...regularQuestionIds, ...caseStudyQuestionIds]

        return {
          section,
          question_ids: allQuestionIds,
        }
      })
    return {
      name: practiceTestName.trim(),
      status: statusForUpdate,
      general_information: generalInformation,
      duration: Number(durationMinutes) || 0,
      sections,
    }
  }

  async function handleSaveDraft() {
    try {
      if (!practiceTestName.trim()) {
        toast.error('Mock exam name is required', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }

      if (practiceTestName.trim().length > 100) {
        toast.error('Mock exam name must not exceed 100 characters', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }

      if (practiceTestId && refetchData) {
        const body = buildUpdateBody(PracticeTestStatus.DRAFT)
        await updatePracticeTest({ id: practiceTestId, body })

        // Invalidate related queries to refetch updated data
        queryClient.invalidateQueries({
          queryKey: ['practiceTestDetail', String(practiceTestId)],
        })
        queryClient.invalidateQueries({ queryKey: ['practiceTests'] })
        queryClient.invalidateQueries({
          queryKey: ['draft-case-study-questions', String(practiceTestId)],
        })

        toast.success('Draft updated', {
          duration: 3000,
          position: 'top-right',
        })

        refetchData()
        return
      }

      // Collect all question IDs - selectedBySection already includes case-study questions from buildAllIds
      const allSelectedIds = Object.values(selectedBySection).flat()
      const numericIds = allSelectedIds
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n)) as number[]

      const response = await createPracticeTest({
        question_ids: numericIds,
        is_mock_exam: true,
        practice_test_name: practiceTestName.trim(),
        general_information: generalInformation,
        module_id: Number(moduleId),
        owner_id: Number(user?.id),
      })
      setPracticeTestId(response.id)

      // Invalidate related queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['practiceTests'] })
      queryClient.invalidateQueries({
        queryKey: ['practiceTestDetail', String(response.id)],
      })

      navigate({
        to: '/create-mock-exam/detail/$ptId/$moduleId',
        params: { ptId: response.id, moduleId },
      })
      toast.success('Draft saved', { duration: 3000, position: 'top-right' })
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save draft'
      toast.error(message, { duration: 3000, position: 'top-right' })
    }
  }

  async function handleSavePublish() {
    try {
      // Collect all question IDs - selectedBySection already includes case-study questions from buildAllIds
      const allSelectedIds = Object.values(selectedBySection).flat()
      const numericIds = allSelectedIds
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n)) as number[]

      if (!practiceTestName.trim()) {
        toast.error('Mock exam name is required', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }
      if (practiceTestName.trim().length > 100) {
        toast.error('Mock exam name must not exceed 100 characters', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }
      if (numericIds.length === 0) {
        toast.error('Please select at least one question before publishing', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }

      // Validate: Each required section (A, B, and C if required) has at least 1 question or 1 case study
      const missingSections = requiredSections.filter((sec) => {
        const hasQuestions = (sectionTotals[sec]?.totalQuestions || 0) > 0
        const hasCaseStudies = (caseStudyGroupsBySection[sec] || []).some(
          (g) =>
            (g?.selectedCount || 0) > 0 ||
            (g?.items?.length || 0) > 0 ||
            (g?.totalMarks || 0) > 0
        )
        return !hasQuestions && !hasCaseStudies
      })
      if (missingSections.length > 0) {
        toast.error(
          `Each section must have at least 1 question or 1 case study.`,
          {
            duration: 3000,
            position: 'top-right',
          }
        )
        return
      }

      const { sumM, sumT } = computeSums(sectionTotals)
      if (sumT < 150 || sumT > 180) {
        toast.error('Mock exam duration must be between 150-180 minutes', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }
      if (sumM !== 100) {
        toast.error('Mock exam total marks must be exactly 100', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }

      let draftId = practiceTestId
      if (!draftId) {
        const response = await createPracticeTest({
          question_ids: numericIds,
          is_mock_exam: true,
          practice_test_name: practiceTestName.trim(),
          general_information: generalInformation,
          module_id: Number(moduleId),
          owner_id: Number(user?.id),
        })
        draftId = response.id
        setPracticeTestId(response.id)
      } else {
        const updateBody = buildUpdateBody(PracticeTestStatus.DRAFT)
        await updatePracticeTest({ id: draftId, body: updateBody })
      }

      const res = await publishPracticeTest(draftId)

      // Invalidate related queries to refetch updated data
      if (refetchData) {
        refetchData()
      }

      queryClient.invalidateQueries({ queryKey: ['practiceTests'] })
      queryClient.invalidateQueries({
        queryKey: ['practiceTestDetail', String(res.id)],
      })
      queryClient.invalidateQueries({
        queryKey: ['prac_test_detail', String(res.id)],
      })
      queryClient.invalidateQueries({
        queryKey: ['practiceTestDetail', String(res.id), 'paged'],
      })
      await queryClient.refetchQueries({
        queryKey: ['practiceTestDetail', String(res.id)],
      })

      toast.success('Published', { duration: 3000, position: 'top-right' })
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save publish'
      toast.error(message, { duration: 3000, position: 'top-right' })
    }
  }

  async function handleDeleteDraft() {
    try {
      if (!practiceTestId || !status || status !== PracticeTestStatus.DRAFT) {
        toast.error('Mock exam id is required', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }
      await deleteDraftPracticeTest(practiceTestId)

      // Invalidate related queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['practiceTests'] })
      queryClient.invalidateQueries({
        queryKey: ['practiceTestDetail', String(practiceTestId)],
      })

      toast.success('Draft deleted', { duration: 3000, position: 'top-right' })
      setIsDeleteOpen(false)
      navigate({
        to: '/test/$moduleId',
        params: { moduleId },
      })
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to delete draft'
      toast.error(message, { duration: 3000, position: 'top-right' })
    }
  }

  function handleBackClick() {
    const hasChanges =
      practiceTestName.trim() ||
      Object.values(selectedBySection).flat().length > 0
    if (hasChanges) {
      setIsBackConfirmOpen(true)
    } else {
      navigate({
        to: '/test/$moduleId',
        params: { moduleId },
      })
    }
  }

  function handleCancelAnyway() {
    setIsBackConfirmOpen(false)
    navigate({
      to: '/test/$moduleId',
      params: { moduleId },
    })
  }

  async function handleSaveDraftAndExit() {
    try {
      if (!practiceTestName.trim()) {
        toast.error('Mock exam name is required', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }
      if (practiceTestName.trim().length > 100) {
        toast.error('Mock exam name must not exceed 100 characters', {
          duration: 3000,
          position: 'top-right',
        })
        return
      }

      if (practiceTestId) {
        const body = buildUpdateBody(PracticeTestStatus.DRAFT)
        await updatePracticeTest({ id: practiceTestId, body })
        navigate({
          to: '/test/$moduleId',
          params: { moduleId },
        })

        // Invalidate related queries to refetch updated data
        queryClient.invalidateQueries({
          queryKey: ['practiceTestDetail', String(practiceTestId)],
        })
        queryClient.invalidateQueries({ queryKey: ['practiceTests'] })
        queryClient.invalidateQueries({
          queryKey: ['draft-case-study-questions', String(practiceTestId)],
        })

        toast.success('Draft saved', { duration: 3000, position: 'top-right' })
      } else {
        // Collect all question IDs - selectedBySection already includes case-study questions from buildAllIds
        const allSelectedIds = Object.values(selectedBySection).flat()
        const numericIds = allSelectedIds
          .map((id) => Number(id))
          .filter((n) => Number.isFinite(n)) as number[]

        const response = await createPracticeTest({
          question_ids: numericIds,
          is_mock_exam: true,
          practice_test_name: practiceTestName.trim(),
          general_information: generalInformation,
          module_id: Number(moduleId),
          owner_id: Number(user?.id),
        })
        navigate({
          to: '/test/$moduleId',
          params: { moduleId },
        })

        // Invalidate related queries to refetch updated data
        queryClient.invalidateQueries({ queryKey: ['practiceTests'] })
        queryClient.invalidateQueries({
          queryKey: ['practiceTestDetail', String(response.id)],
        })

        toast.success('Draft saved', { duration: 3000, position: 'top-right' })
      }

      setIsBackConfirmOpen(false)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save draft'
      toast.error(message, { duration: 3000, position: 'top-right' })
    }
  }

  async function handleOpenDuplicate() {
    try {
      // Ensure we have a saved draft before duplicating
      // Collect all question IDs - selectedBySection already includes case-study questions from buildAllIds
      // const allSelectedIds = Object.values(selectedBySection).flat()
      // const numericIds = allSelectedIds
      //   .map((id) => Number(id))
      //   .filter((n) => Number.isFinite(n)) as number[]

      // if (!practiceTestName.trim()) {
      //   toast.error('Mock exam name is required', {
      //     duration: 3000,
      //     position: 'top-right',
      //   })
      //   return
      // }

      // if (numericIds.length === 0) {
      //   toast.error('Please select at least one question to duplicate', {
      //     duration: 3000,
      //     position: 'top-right',
      //   })
      //   return
      // }

      // if (!practiceTestId) {
      //   const response = await createPracticeTest({
      //     question_ids: numericIds,
      //     is_mock_exam: true,
      //     practice_test_name: practiceTestName.trim(),
      //     general_information: generalInformation,
      //     module_id: Number(moduleId),
      //     owner_id: Number(user?.id),
      //   })
      //   setPracticeTestId(response.id)
      // } else {
      //   const updateBody = buildUpdateBody(PracticeTestStatus.DRAFT)
      //   await updatePracticeTest({ id: practiceTestId, body: updateBody })

      //   // Invalidate related queries to refetch updated data
      //   queryClient.invalidateQueries({
      //     queryKey: ['practiceTestDetail', practiceTestId],
      //   })
      //   queryClient.invalidateQueries({ queryKey: ['practiceTests'] })
      //   queryClient.invalidateQueries({
      //     queryKey: ['draft-case-study-questions', practiceTestId],
      //   })
      // }

      setIsDuplicateOpen(true)
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to prepare duplication'
      toast.error(message, { duration: 3000, position: 'top-right' })
    }
  }

  async function handleConfirmDuplicate() {
    try {
      const res = await duplicatePracticeTest({
        practice_test_id: Number(practiceTestId),
        practice_test_name: practiceTestName.trim(),
        general_information: generalInformation,
        class_id: [],
        user_id: Number(user?.id),
        module_id: Number(moduleId),
      })

      toast.success(`Duplicated test`, {
        duration: 3000,
        position: 'top-right',
      })

      navigate({
        to: '/create-practice-test/detail/$ptId/$moduleId',
        params: { ptId: res?.[0]?.id, moduleId },
      })
      setIsDuplicateOpen(false)
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to duplicate practice test'
      toast.error(message, { duration: 3000, position: 'top-right' })
    }
  }

  useEffect(() => {
    setIsActive(false)
    return () => setIsActive(true)
  }, [setIsActive])

  // Track if user has manually set duration via input
  // If initDuration is provided and > 0, assume it is user-set (or at least fixed from API)
  const [hasUserSetDuration, setHasUserSetDuration] = useState(
    !!(initDuration && initDuration > 0)
  )
  const [userSetDuration, setUserSetDuration] = useState<number | undefined>(
    undefined
  )

  const hasSetInitDurationRef = useRef(false)
  const prevSumTRef = useRef<number | null>(null)

  // First load only: set initDuration once
  useEffect(() => {
    if (!hasSetInitDurationRef.current && initDuration !== undefined) {
      setDurationMinutes(initDuration)
      if (initDuration > 0) {
        setHasUserSetDuration(true)
      }
      hasSetInitDurationRef.current = true
    }
  }, [initDuration])

  useEffect(() => {
    const { sumQ, sumM, sumT } = computeSums(sectionTotals)

    // Always update totals
    if (Object.keys(sectionTotals).length > 0) {
      setTotalQuestions(sumQ)
      setTotalMarks(sumM)

      // Auto-update duration only if:
      // 1. User hasn't manually input it
      // 2. sumT has actually changed (not first load)
      const sumTChanged =
        prevSumTRef.current !== null && prevSumTRef.current !== sumT

      if (!hasUserSetDuration && sumTChanged) {
        setDurationMinutes(sumT)
      } else if (
        !hasUserSetDuration &&
        initDuration === undefined &&
        prevSumTRef.current === null
      ) {
        // Initial load for new test
        setDurationMinutes(sumT)
      }

      // Update previous sumT
      prevSumTRef.current = sumT
    }
  }, [sectionTotals, hasUserSetDuration, initDuration])

  // Update duration when sections change, but only if user hasn't manually set it
  useEffect(() => {
    if (hasUserSetDuration && userSetDuration !== undefined) {
      setDurationMinutes(userSetDuration)
    }
  }, [userSetDuration, hasUserSetDuration])

  const practiceTestDetailData = useMemo<PracticeTestDetail>(() => {
    const sectionKeys = Array.from(
      new Set([
        ...Object.keys(selectedItemsBySection),
        ...Object.keys(caseStudyGroupsBySection),
        ...Object.keys(sectionTotals),
      ])
    )

    const makeMeta = (total: number) => ({
      items_per_page: total,
      total_items: total,
      current_page: 1,
      total_pages: 1,
    })

    const sections = sectionKeys.map((sec) => {
      const totals = sectionTotals[sec] || {
        totalQuestions: 0,
        totalMarks: 0,
        totalTime: 0,
      }
      const qData = (selectedItemsBySection[sec] || []) as QuestionDetail[]
      const csGroups = (caseStudyGroupsBySection[sec] || []).map((g) => ({
        case_study_id: g.caseStudyId,
        case_study: '',
        total_questions: g.selectedCount || 0,
        total_duration: g.totalTime || 0,
        total_mark: g.totalMarks || 0,
      }))

      return {
        name: sec,
        total_marks: totals.totalMarks || 0,
        total_time: totals.totalTime || 0,
        total_questions: totals.totalQuestions || 0,
        questions: { data: qData, meta: makeMeta(qData.length) } as any,
        case_studies: {
          data: csGroups,
          meta: makeMeta(csGroups.length),
        } as any,
      }
    })

    return {
      pt_id: Number(practiceTestId) || 0,
      pt_name: practiceTestName || 'Mock Exam',
      module_name: '',
      status: PTStatus.DRAFT,
      general_information: generalInformation,
      owner: String(user?.id),
      sections,
    }
  }, [
    practiceTestId,
    practiceTestName,
    user?.id,
    selectedItemsBySection,
    caseStudyGroupsBySection,
    sectionTotals,
    generalInformation,
  ])

  const hasAnySelected = useMemo(() => {
    // Prefer using sectionTotals if available as the source of truth
    if (Object.keys(sectionTotals).length > 0) {
      return Object.values(sectionTotals).some(
        (t) => (t?.totalQuestions || 0) > 0
      )
    }
    const anyRegular = Object.values(selectedBySection).some(
      (arr) => (arr?.length || 0) > 0
    )
    const anyCase = Object.values(caseStudyGroupsBySection).some((groups) =>
      (groups || []).some((g) => (g?.selectedCount || 0) > 0)
    )
    return anyRegular || anyCase
  }, [sectionTotals, selectedBySection, caseStudyGroupsBySection])

  return (
    <Stack gap={'24px'} className=''>
      <div className='sticky top-0 z-50 bg-[#00397A]'>
        <Hstack
          height={'72px'}
          gap={'32px'}
          align={'center'}
          justify={'between'}
          className='ml-[-16px] px-[40px]'
        >
          <Hstack
            py={'10px'}
            className={hoverPressable}
            style={{ cursor: 'pointer' }}
            onClick={handleBackClick}
          >
            <IconArrowLeft width={'25px'} height={'25px'} color='#F6F7F9' />
            <Text className='text-[16px] font-medium text-[#F6F7F9]'>Back</Text>
          </Hstack>
          <Hstack gap={'12px'} className='p-[10px]'>
            <Button
              className={`bg-[#FFFFFF26] text-white hover:bg-[#FFFFFF26] ${hoverPressable}`}
              onClick={() => setIsPreviewOpen(true)}
              disabled={!hasAnySelected}
            >
              Preview
            </Button>
            <Button
              className={`border border-[##FFFFFF] bg-transparent text-white hover:bg-transparent ${hoverPressable}`}
              onClick={handleSaveDraft}
              disabled={!practiceTestName.trim() || isCreating || isUpdating}
            >
              Save draft
            </Button>
            {status === PracticeTestStatus.DRAFT && (
              <Button
                className={`border bg-red-700 text-white hover:bg-transparent ${hoverPressable}`}
                onClick={() => setIsDeleteOpen(true)}
                disabled={isDeletingDraft}
              >
                <IconX />
                Delete
              </Button>
            )}
            <Button
              className={`border border-[##FFFFFF] bg-[#D4D4D4D1] text-[#002652] ${hoverPressable}`}
              onClick={handleOpenDuplicate}
              disabled={isCreating || isUpdating || !practiceTestId}
            >
              Duplicate
            </Button>
            <Button
              className={`bg-white text-[#002652] hover:bg-white ${hoverPressable}`}
              onClick={handleSavePublish}
              disabled={
                !practiceTestId ||
                // !practiceTestName.trim() ||
                // Object.values(selectedBySection).flat().length === 0 ||
                isCreating ||
                isPublishing ||
                isUpdating
              }
            >
              Publish
            </Button>
          </Hstack>
        </Hstack>
      </div>
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className='md:max-w-5xl lg:max-w-6xl'>
          <div className='flex items-center justify-between'>
            <h3 className='text-[20px] font-medium text-[#002652]'>
              Preview: {practiceTestDetailData?.pt_name || 'Mock Exam'}
            </h3>
            <button
              className='text-[#475467] hover:text-[#002652]'
              onClick={() => setIsPreviewOpen(false)}
            ></button>
          </div>
          <div className='max-h-[70vh] overflow-auto rounded-md border bg-white'>
            <div className='p-4'>
              <Preview practiceTestData={practiceTestDetailData as any} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title={
          <div className='flex w-full justify-center'>
            <div className='flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 text-gray-600'>
              <IconInfoCircle size={40} />
            </div>
          </div>
        }
        desc={
          <div className='text-center'>Are you sure to delete this draft?</div>
        }
        cancelBtnText='Cancel'
        confirmText='Yes'
        destructive
        isLoading={isDeletingDraft}
        handleConfirm={handleDeleteDraft}
        className='max-w-md'
      />
      <Dialog open={isDuplicateOpen} onOpenChange={setIsDuplicateOpen}>
        <DialogContent className='px-0 pt-5 sm:max-w-2xl'>
          <Stack gap={'16px'}>
            <Hstack
              justify={'between'}
              className='border-b border-[#E5E5E5] pb-[25px]'
            >
              <h3 className='px-6 text-[20px] font-medium text-[#002652]'>
                Duplicate {practiceTestDetailData?.pt_name || 'Practice Test'}
              </h3>
            </Hstack>

            <div className='flex w-full flex-col items-center gap-10 text-sm text-[#002652]'>
              <p>Are you sure to duplicate this test?</p>
              <p>Duplicated test will be in Draft.</p>
            </div>
            <Hstack justify={'end'} gap={'8px'} className='px-6'>
              <Button
                className='border border-[#EAECF0] bg-white text-[#475467] hover:bg-transparent'
                onClick={() => setIsDuplicateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className='min-w-[94px] text-white'
                onClick={handleConfirmDuplicate}
                disabled={isDuplicating}
              >
                {isDuplicating ? <Spinner /> : <>Duplicate</>}
              </Button>
            </Hstack>
          </Stack>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        handleClose={() => setIsBackConfirmOpen(false)}
        open={isBackConfirmOpen}
        onOpenChange={(open) => {
          if (!open) handleCancelAnyway()
        }}
        title={
          <div className='flex w-full justify-center'>
            <div className='flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 text-gray-600'>
              <IconInfoCircle size={40} />
            </div>
          </div>
        }
        desc={
          <div className='text-center'>
            <div className='mb-2 font-medium text-gray-900'>
              Canceling now will discard your progress.
            </div>
            <div className='text-gray-600'>
              Do you want to save a draft before canceling?
            </div>
          </div>
        }
        cancelBtnText='Cancel anyway'
        confirmText='Save draft'
        isLoading={isCreating || isUpdating}
        handleConfirm={handleSaveDraftAndExit}
        className='max-w-md'
      />
      <CreateView
        moduleId={moduleId}
        name={practiceTestName}
        onNameChange={setPracticeTestName}
        generalInfo={generalInformation}
        onGeneralInfoChange={setGeneralInformation}
        status={status}
        totalQuestions={totalQuestions}
        totalMarks={totalMarks}
        durationMinutes={durationMinutes}
        onDurationChange={(value) => {
          setDurationMinutes(value)
          setUserSetDuration(value)
          setHasUserSetDuration(true)
        }}
        initialSelectedQuestions={initialSelectedQuestions}
        initialSelectedCaseStudies={initialSelectedCaseStudies}
        initialTitle={initialPracticeTestName}
        onSaveSelected={({
          section,
          questionIds,
          items,
          caseStudyGroups,
          totals,
        }) => {
          setSelectedBySection((prev) => ({ ...prev, [section]: questionIds }))
          setSelectedItemsBySection((prev) => ({ ...prev, [section]: items }))
          setCaseStudyGroupsBySection((prev) => ({
            ...prev,
            [section]: caseStudyGroups || [],
          }))
          setSectionTotals((prev) => ({
            ...prev,
            [section]: totals || {
              totalQuestions: 0,
              totalMarks: 0,
              totalTime: 0,
            },
          }))

          // Reset user set duration flag when sections change to allow auto-update
          if (hasUserSetDuration) {
            setHasUserSetDuration(false)
            setUserSetDuration(undefined)
          }
        }}
      />
      <div className='hidden' />
    </Stack>
  )
}
